import { LOCATION_INITIALIZED } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injector, inject } from '@angular/core';
import { TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import * as Sentry from '@sentry/angular-ivy';
import { Observable, catchError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { LanguageService } from '../../core/services/language.service';
import { UserConfigurationService } from '../../core/services/user-configuration.service';
import { WhitelabelManagerService } from '../../whitelabel/services/whitelabel-manager.service';
import { Languages } from '../enums/languages.enum';

export function TranslateHttpLoaderFactory(http: HttpClient): TranslateLoader {
  return new S3TranslateHttpLoader(http);
}

export class S3TranslateHttpLoader extends TranslateHttpLoader {

  whitelabelManagerService = inject(WhitelabelManagerService);
  private userConfigurationService = inject(UserConfigurationService);

  constructor(http: HttpClient, prefix?: string, suffix?: string) {
    super(http, prefix, suffix);
  }

  public override getTranslation(lang: string): Observable<object> {
    if (lang === Languages.DEV) {
      // Weblate allow only file with this name as base translations file (dev-DEV replaced with en_devel)
      lang = 'en_devel';
    }

    return this.loadTranslationsFromS3(lang)
      .pipe(
        catchError(() => this.loadTranslationsFromS3(this.userConfigurationService.getFallbackLanguage())),
        catchError(() => super.getTranslation(lang))
      );
  }

  private loadTranslationsFromS3(lang: string): Observable<object> {
    return new Observable((subscriber) => {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', `${environment.APP_TRANSLATION_BASE_URL}/${lang}.json`);
      xhr.setRequestHeader('x-api-key', environment.APP_TRANSLATION_API_KEY);
      xhr.onload = () => {
        if ((xhr.status >= 200 && xhr.status < 300) || xhr.status === 304) {
          // inject whitelabeled translation overrides into translation object
          const translations = this.whitelabelManagerService.whitelabelTranslations(JSON.parse(xhr.response));
          subscriber.next(translations);
        } else {
          subscriber.error(xhr.statusText);
        }
      };
      xhr.onerror = () => subscriber.error(xhr.statusText);
      xhr.onloadend = () => subscriber.complete();
      xhr.send();
    });
  }
}

export function appInitializerTranslationFactory(languageService: LanguageService, injector: Injector) {
  return () =>
    new Promise((resolve) => {
      const locationInitialized = injector.get(LOCATION_INITIALIZED, Promise.resolve(null));
      locationInitialized.then(() => {
        const language = languageService.getCurrentLanguage();
        languageService.use(language).subscribe({
          next: () => { },
          error: () => Sentry.captureMessage(`Problem with '${language}' language initialization.`, 'warning'),
          complete: () => resolve(null),
        });
      });
    });
}
